import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, Form, Input, message, Radio, Select, Typography, Col } from "antd";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { faqService } from "../../../services/faq.service";
import { faqCategoryService } from "../../../services/faq-category.service";
import CardFooterAction from "../../../components/CardFooterAction";
import JoditEditor from "jodit-react";
import ImageUploader from "../../../components/ImageUpload";
import { API_BASE_URL } from "../../../config/api.config";

export default function FaqForm(){
  const { id } = useParams();
  const [form] = useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [imageUrl, setImageUrl] = useState(null);
  const { Text } = Typography;

  const { data: faq } = useQuery({
    queryKey: ['detailFaq', id],
    queryFn: () => faqService.getById(id),
    enabled: !!id
  });

  const { data: pinnedData } = useQuery({
    queryKey: ['pinnedFaq'],
    queryFn: () => faqService.countPinned(),
  });

  const { data: faqCategories } = useQuery({
    queryKey: ['faqCategories'],
    queryFn: () => faqCategoryService.fetchAllWithoutPaginate()
  });

  // Initialize the form with default values
  useEffect(() => {
    // Set default values for new records
    if (!id) {
      form.setFieldsValue({
        isPinned: false,
        isActive: true
      });
    }
  }, [form, id]);

  useEffect(() => {
    if(id && faq?.data){
      form.setFieldsValue(faq?.data);
    }
  }, [faq, form, id]);

  useEffect(() => {
    if (faq?.data?.image) {
      setImageUrl(`${API_BASE_URL}/${faq.data.image}`);
    }
  }, [faq]);

  const storeMutation = useMutation({
    mutationFn: (data) => faqService.store(data),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      message.success('Data berhasil disimpan');
      form.resetFields();
      navigate('/customer-care/faq');
    },
    onError: (error) => {
      console.log(error);
      message.error('Terjadi Kesalahan');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, formData }) => faqService.update(id, formData),
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      message.success('Data berhasil diubah');
      form.resetFields();
      navigate('/customer-care/faq');
    },
    onError: (error) => {
      console.log(error);
      message.error('Terjadi Kesalahan');
    }
  });

  const handleSubmit = (values) => {
    // Get the image file from the form
    const imageFile = form.getFieldValue('image');
    
    // Create a FormData object to handle the form data
    const formData = new FormData();
    
    // Add each field to the FormData using hyphens instead of underscores
    // to match the backend API expectations
    if (id) {
      formData.append('id', id);
    }
    
    formData.append('category_id', values.category_id); // Use hyphen instead of underscore
    formData.append('question', values.question);
    formData.append('answer', values.answer);
    formData.append('isActive', values.isActive);
    
    // Ensure isPinned is always explicitly set to true or false
    formData.append('isPinned', values.isPinned === true);
    
    // Add image file if it exists
    if (imageFile instanceof File) {
      formData.append('image', imageFile);
    }
    
    if (!id) {
      storeMutation.mutate(formData);
    } else {
      updateMutation.mutate({ id, formData });
    }
  };

  // Check if this FAQ can be pinned
  const canBePinned = id ? faq?.data?.isPinned : pinnedData?.data < 3;

  return (
    <>
      <Card
        title={ (id ? "Ubah" : "Tambah") + " Data"}
        actions={[
          <CardFooterAction
            key={'1'}
            route={'/customer-care/faq'}
            onClick={() => form.submit()}
          />
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
        >
          <Form.Item hidden name={'id'} >
            <Input/>
          </Form.Item>
          <Form.Item
            name={'category_id'}
            label="Kategori"
            rules={[
              {
                required: true,
                message: 'Kategori tidak boleh kosong'
              }
            ]}
          >
            <Select
              options={faqCategories?.data?.map(e => ({ value: e.id, label: e.name }))}
            />
          </Form.Item>

          <Col xs={24} md={12} style={{ marginTop: 15 }}>
            <ImageUploader
              form={form}
              name="image"
              label="Gambar"
              imageFromAPI={imageUrl}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "12px",
                display: "block",
                wordBreak: "break-word",
              }}
            >
              Max file size 2MB, tipe file .jpg, .png, & .webp, Ukuran 240 x 480px
            </Text>
          </Col>

          <Form.Item
            name={'question'}
            label="Pertanyaan"
            style={{ marginTop: 20 }}
            rules={[
              {
                required: true,
                message: 'Pertanyaan tidak boleh kosong'
              }
            ]}
          >
            <Input/>
          </Form.Item>
          <Form.Item
            name={'answer'}
            label="Jawaban"
            rules={[
              {
                required: true,
                message: 'Jawaban tidak boleh kosong'
              }
            ]}
          >
            <JoditEditor
              config={{
                showStatusBar: false,
                footer: false,
                uploader: { insertImageAsBase64URI: true },
                height: 500
              }}
            />
          </Form.Item>
          <Form.Item
            name={'isActive'}
            label="Status"
            rules={[
              {
                required: true,
                message: 'Status tidak boleh kosong'
              }
            ]}
          >
            <Radio.Group>
              <Radio style={{ marginRight: 80}} value={true} >Active</Radio>
              <Radio value={false} >Inactive</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name={'isPinned'}
            label="Pin Ke Beranda"
            rules={[
              {
                required: true,
                message: 'Pilihan Pin Ke Beranda tidak boleh kosong'
              }
            ]}
          >
            <Radio.Group disabled={!canBePinned}>
              <Radio style={{ marginRight: 80}} value={true}>Ya</Radio>
              <Radio value={false}>Tidak</Radio>
            </Radio.Group>
            {!canBePinned && !id && (
              <Text type="warning" style={{ display: 'block', marginTop: 5 }}>
                Maksimum 3 FAQ dapat di-pin ke beranda
              </Text>
            )}
          </Form.Item>
        </Form>
      </Card>
    </>
  );
}